import { useState, useEffect } from "react";
import { GoogleMap } from "@react-google-maps/api";
import { Circle, Polyline, Marker } from "../../../theme/maps";
import { Box, Typography, TextField } from "@mui/material";
import { colors } from "../../../theme/muiColors";
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from "react-places-autocomplete";
import { getMyLocation } from "../../../utils";
import { calculateDistance } from "./helpers/calculateDistance";

export const ResidentialCalculatorArea = ({ setValue }) => {
  const [coverageAreas, setCoverageAreas] = useState([]);
  const [map, setMap] = useState(null);
  const [address, setAddress] = useState("");
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [closestAreaId, setClosestAreaId] = useState(null);
  const [inCoverage, setInCoverage] = useState(false);
  const [inIgnore, setInIgnore] = useState(false);
  const [extraDistance, setExtraDistance] = useState(0); // miles

  const [ignoredAreaRate, setIgnoredAreaRate] = useState("");
  const [surchargeValue, setSurchargeValue] = useState("");

  const token = sessionStorage.getItem("token");

  const defaultPosition = { lat: 37.7749, lng: -122.4194 };
  const [mapCenter, setMapCenter] = useState(defaultPosition);
  const [initializedCenter, setInitializedCenter] = useState(false);

  const normalizeIgnore = (ig) => ({
    id: ig.id,
    lat: Number(ig.lat ?? ig.latitude),
    lng: Number(ig.lng ?? ig.longitude),
    radius: Number(ig.radius ?? ig.milesOfCoverageArea) || 0,
    name: ig.name || "Ignore",
  });

  const handleMapClick = (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    const newLocation = { lat, lng };

    setSelectedLocation(newLocation);

    setValue("latitude", lat, { shouldtouch: true, shouldValidate: true });
    setValue("longitude", lng, { shouldtouch: true, shouldValidate: true });

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: newLocation }, (results, status) => {
      if (status === "OK" && results[0]) {
        setAddress(results[0].formatted_address);
      }
    });

    checkCoverage(newLocation);
  };

  useEffect(() => {
    getMyLocation(token).then((res) => {
      setCoverageAreas(res || []);
      if (res?.length && !initializedCenter) {
        setMapCenter({ lat: res[0].lat, lng: res[0].lng });
        setInitializedCenter(true);
      }
    });
  }, [token, initializedCenter]);

  const handleSelect = async (value) => {
    setAddress(value);
    try {
      const results = await geocodeByAddress(value);
      const latLng = await getLatLng(results[0]);
      setSelectedLocation(latLng);

      setValue("latitude", latLng.lat, { shouldtouch: true, shouldValidate: true });
      setValue("longitude", latLng.lng, { shouldtouch: true, shouldValidate: true });

      checkCoverage(latLng);
    } catch (error) {
      console.error("Error al obtener la dirección:", error);
    }
  };

  const handleDestinationDragEnd = (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    const newLocation = { lat, lng };
    setSelectedLocation(newLocation);

    setValue("latitude", lat, { shouldtouch: true, shouldValidate: true });
    setValue("longitude", lng, { shouldtouch: true, shouldValidate: true });

    checkCoverage(newLocation);
  };

  const checkCoverage = (location) => {
    if (!location || coverageAreas.length === 0) return;

    let closestArea = null;
    let minDistance = Infinity;

    coverageAreas.forEach((area) => {
      const d = calculateDistance(area.lat, area.lng, location.lat, location.lng);
      if (d < minDistance) {
        minDistance = d;
        closestArea = area;
      }
    });

    if (!closestArea) return;

    setClosestAreaId(closestArea.id);

    const insideCoverage = minDistance <= Number(closestArea.radius);

    if (insideCoverage) {
      const ignores = (closestArea.ignoreAreas || []).map(normalizeIgnore);
      const isInIgnore = ignores.some((ig) => {
        const d = calculateDistance(ig.lat, ig.lng, location.lat, location.lng);
        return d <= ig.radius;
      });

      setInCoverage(true);
      setInIgnore(isInIgnore);
      setExtraDistance(0);

      setValue("inCoverage", true, { shouldtouch: true, shouldValidate: true });
      setValue("inIgnoreArea", !!isInIgnore, { shouldtouch: true, shouldValidate: true });
      setValue("extraDistanceMiles", 0, { shouldtouch: true, shouldValidate: true });

      if (isInIgnore) {
        setSurchargeValue("");
        setValue("surchargeValue", "", { shouldtouch: true, shouldValidate: true });
      } else {
        setSurchargeValue("");
        setIgnoredAreaRate("");
        setValue("surchargeValue", "", { shouldtouch: true, shouldValidate: true });
        setValue("ignoredAreaRate", "", { shouldtouch: true, shouldValidate: true });
      }
    } else {
      setInCoverage(false);
      setInIgnore(false);

      const milesOutside = parseFloat(((minDistance - Number(closestArea.radius)) / 1609.34).toFixed(1));
      setExtraDistance(milesOutside);

      setValue("inCoverage", false, { shouldtouch: true, shouldValidate: true });
      setValue("inIgnoreArea", false, { shouldtouch: true, shouldValidate: true });
      setValue("extraDistanceMiles", milesOutside, { shouldtouch: true, shouldValidate: true });

      setIgnoredAreaRate("");
      setValue("ignoredAreaRate", "", { shouldtouch: true, shouldValidate: true });
    }
  };

  // Sincroniza a RHF
  useEffect(() => {
    setValue("surchargeValue", surchargeValue, { shouldtouch: true, shouldValidate: true });
  }, [surchargeValue, setValue]);

  useEffect(() => {
    setValue("ignoredAreaRate", ignoredAreaRate, { shouldtouch: true, shouldValidate: true });
  }, [ignoredAreaRate, setValue]);

  return (
    <Box sx={{ mt: 4, width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" }, // 📌 mapa izquierda / inputs derecha
          gap: 2,
          width: "100%",
        }}
      >
        {/* Mapa */}
        <Box sx={{ flex: 2, minHeight: 500 }}>
          <GoogleMap
            mapContainerStyle={{
              width: "100%",
              height: "500px",
              borderRadius: "8px",
              border: `1px solid ${colors.grey[300]}`,
            }}
            center={mapCenter}
            zoom={10}
            onLoad={(m) => setMap(m)}
            onClick={handleMapClick}
          >
            {/* Coverage areas */}
            {coverageAreas.map((area, index) => (
              <Circle
                key={`cov-${area.id || index}`}
                center={{ lat: area.lat, lng: area.lng }}
                radius={Number(area.radius)}
                options={{
                  strokeColor: "#FF0000",
                  strokeOpacity: 0.8,
                  strokeWeight: 2,
                  fillColor: "#FF0000",
                  fillOpacity: 0.15,
                  clickable: false,
                  zIndex: 1,
                }}
              />
            ))}

            {/* Ignore areas */}
            {coverageAreas.flatMap((area, aidx) =>
              (area.ignoreAreas || []).map((ig, iidx) => {
                const n = normalizeIgnore(ig);
                return (
                  <Circle
                    key={`ign-${n.id || `${aidx}-${iidx}`}`}
                    center={{ lat: n.lat, lng: n.lng }}
                    radius={n.radius}
                    options={{
                      strokeColor: "#6A5ACD",
                      strokeOpacity: 0.9,
                      strokeWeight: 2,
                      fillColor: "#6A5ACD",
                      fillOpacity: 0.25,
                      clickable: false,
                      zIndex: 2,
                    }}
                  />
                );
              }),
            )}

            {selectedLocation && <Marker position={selectedLocation} draggable onDragEnd={handleDestinationDragEnd} />}

            {selectedLocation &&
              closestAreaId &&
              (() => {
                const ca = coverageAreas.find((a) => a.id === closestAreaId);
                if (!ca) return null;
                return (
                  <Polyline
                    path={[{ lat: ca.lat, lng: ca.lng }, selectedLocation]}
                    options={{
                      strokeColor: "#0000FF",
                      strokeOpacity: 0.9,
                      strokeWeight: 3,
                    }}
                  />
                );
              })()}
          </GoogleMap>
        </Box>

        {/* Panel a la derecha */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Estado del destino */}
          {selectedLocation && (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: `1px solid ${inIgnore ? "#ff9800" : inCoverage ? "#4caf50" : "#ff9800"}`,
                backgroundColor: inIgnore ? "rgba(255, 152, 0, 0.05)" : inCoverage ? "rgba(76, 175, 80, 0.05)" : "rgba(255, 152, 0, 0.05)",
                height: "fit-content",
              }}
            >
              {inIgnore ? (
                <>
                  <Typography variant="subtitle2" sx={{ color: "#e65100", fontWeight: 600 }}>
                    ⚠️ In ignore area — you may need an extra fee?
                  </Typography>
                  <TextField
                    size="small"
                    type="number"
                    label="Ignored area fee ($)"
                    value={ignoredAreaRate}
                    onChange={(e) => setIgnoredAreaRate(e.target.value)}
                    fullWidth
                    inputProps={{ step: "any", min: 0 }}
                    sx={{ mt: 2 }}
                  />
                </>
              ) : inCoverage ? (
                <>
                  <Typography variant="subtitle2" sx={{ color: "#388e3c", fontWeight: 600 }}>
                    ✅ In coverage area
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
                    No extra fee.
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="subtitle2" sx={{ color: "#e65100", fontWeight: 600 }}>
                    ⚠️ Out of coverage area
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
                    Extra distance: <b>{extraDistance} miles</b>
                  </Typography>
                  <TextField
                    size="small"
                    type="number"
                    label="Rate per extra mile ($)"
                    value={surchargeValue}
                    onChange={(e) => setSurchargeValue(e.target.value)}
                    fullWidth
                    inputProps={{ step: "any", min: 0 }}
                    sx={{ mt: 2 }}
                  />
                </>
              )}
            </Box>
          )}

          {/* Autocomplete */}
          <PlacesAutocomplete value={address} onChange={setAddress} onSelect={handleSelect}>
            {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
              <Box sx={{ position: "relative" }}>
                <TextField fullWidth {...getInputProps({ placeholder: "Enter destination address" })} label="Destination" size="small" />
                {(loading || suggestions.length > 0) && (
                  <Box
                    sx={{
                      position: "absolute",
                      zIndex: 10,
                      width: "100%",
                      bgcolor: "var(--fx-surface-base, #FFFFFF)",
                      border: "1px solid var(--fx-surface-border, #E2E8F0)",
                      mt: 1,
                      borderRadius: 1,
                      maxHeight: 200,
                      overflowY: "auto",
                    }}
                  >
                    {loading && (
                      <Typography sx={{ p: 2 }} variant="body2">
                        Loading…
                      </Typography>
                    )}
                    {suggestions.map((suggestion) => {
                      const style = {
                        padding: "10px",
                        cursor: "pointer",
                        backgroundColor: suggestion.active ? "var(--fx-surface-sunken, #F8FAFC)" : "var(--fx-surface-base, #FFFFFF)",
                      };
                      return (
                        <div key={suggestion.placeId || suggestion.description} {...getSuggestionItemProps(suggestion, { style })}>
                          {suggestion.description}
                        </div>
                      );
                    })}
                  </Box>
                )}
              </Box>
            )}
          </PlacesAutocomplete>
        </Box>
      </Box>
    </Box>
  );
};
